import { useId } from 'react';
import { ENGINE_GROUPS, getPath, type FieldSpec } from './fieldSpec';

export type DraftObject = Record<string, unknown>;

export function EngineForm({ draft, errors, onChange }: { draft: DraftObject; errors: Record<string, string>; onChange: (path: string, value: unknown) => void }) {
  return (
    <details className="disclosure">
      <summary>Open the engine</summary>
      <div className="stack">
        {ENGINE_GROUPS.map((g) => (
          <fieldset key={g.key} className="group">
            <legend>{g.label}</legend>
            {g.hint && <div className="hint" style={{ marginBottom: 8 }}>{g.hint}</div>}
            <div className="form-grid">
              {g.fields.map((f) => <Field key={f.path} spec={f} value={getPath(draft, f.path)} error={errors[f.path]} onChange={(v) => onChange(f.path, v)} />)}
            </div>
          </fieldset>
        ))}
      </div>
    </details>
  );
}

export function Field({ spec, value, error, onChange }: { spec: FieldSpec; value: unknown; error?: string; onChange: (v: unknown) => void }) {
  const id = useId();
  if (spec.kind === 'bool') {
    return (
      <label className="check" htmlFor={id}>
        <input id={id} type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        <span>{spec.label}</span>
        {error && <span className="error">{error}</span>}
      </label>
    );
  }
  if (spec.kind === 'enum') {
    return (
      <div className="field">
        <label htmlFor={id}>{spec.label}</label>
        <select id={id} className="select" value={String(value ?? spec.options?.[0] ?? '')} onChange={(e) => {
          const raw = e.target.value;
          onChange(/^\d+$/.test(raw) && spec.options?.every((o) => /^\d+$/.test(o)) ? Number(raw) : raw);
        }}>
          {spec.options?.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <span className="error">{error}</span>}
      </div>
    );
  }
  const num = typeof value === 'number' ? value : Number(value ?? 0);
  return (
    <div className="field">
      <label htmlFor={id}>{spec.label}{spec.unit ? ` (${spec.unit})` : ''}</label>
      <div className="row" style={{ flexWrap: 'nowrap' }}>
        <input type="range" className="range" aria-label={`${spec.label} slider`} min={spec.min} max={spec.max} step={spec.step} value={Number.isFinite(num) ? num : 0}
          onChange={(e) => onChange(spec.kind === 'int' ? Math.round(Number(e.target.value)) : Number(e.target.value))} />
        <input id={id} type="number" className="input" style={{ width: 92, minHeight: 32, padding: '4px 8px' }} min={spec.min} max={spec.max} step={spec.step} value={Number.isFinite(num) ? num : ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
      </div>
      {error && <span className="error">{error}</span>}
    </div>
  );
}
